/**
 * The two content streams, their topics and the labels for both.
 *
 * `TopicLabels` covers every topic of either stream, so `TopicLabels[id]` needs no stream
 * beside it. `MainTopicLabels` / `AdultTopicLabels` are for a control that may only offer
 * the topics of one stream — pick by the stream the record is in, and rebuild the options
 * when it changes, or a form keeps a topic the new stream does not have.
 */
export const Stream = {
  Adult: 1,
  Mainstream: 5
} as const

export const MainTopic = {
  Entertainment: 102,
  FileSharing: 103,
  Finance: 101,
  VideoHosting: 104,
  Other: 5
} as const

export const AdultTopic = {
  Straight: 1,
  Gay: 2,
  Trans: 4,
  AdultToons: 3
} as const

export const Topic = {
  ...MainTopic,
  ...AdultTopic
} as const

export const StreamLabels = {
  [Stream.Adult]: 'Adult',
  [Stream.Mainstream]: 'Mainstream'
} as const

export function streamLabel(stream: number): string {
  return StreamLabels[stream as keyof typeof StreamLabels] ?? String(stream)
}

/**
 * The streams as `Select` options with **string** values, for a filter, whose value rides the
 * URL. Built by hand and not through `labelsToOptions`: this segment may not import it.
 */
export const STREAM_FILTER_OPTIONS = Object.values(Stream).map((id) => ({
  value: String(id),
  label: StreamLabels[id]
}))

export const MainTopicLabels = {
  [MainTopic.Entertainment]: 'Entertainment',
  [MainTopic.FileSharing]: 'File sharing',
  [MainTopic.Finance]: 'Finance',
  [MainTopic.VideoHosting]: 'Video Hosting',
  [MainTopic.Other]: 'Other'
} as const

export const AdultTopicLabels = {
  [AdultTopic.Straight]: 'Straight',
  [AdultTopic.Gay]: 'Gay',
  [AdultTopic.Trans]: 'Trans',
  [AdultTopic.AdultToons]: 'Adult-Toons'
} as const

export type TopicId = (typeof Topic)[keyof typeof Topic]

/**
 * Every topic of both streams in one map. The two streams number their topics apart, so an id
 * alone says which stream it belongs to — a consumer holding a topic never needs the stream
 * beside it.
 */
export const TopicLabels: Record<TopicId, string> = {
  ...AdultTopicLabels,
  ...MainTopicLabels
} as const
